/**
 * Shared utility functions for blog and content handling.
 */
export class Utilities {
    private static getDaySuffix(day: number): string {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    }

    /**
     * Formats an ISO date string for display (e.g. "Jan 5th '25").
     */
    static formatDate(dateString: string): string {
        const date = new Date(dateString);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${month} ${day}${Utilities.getDaySuffix(day)} '${year.toString().slice(-2)}`;
    }

    /**
     * Decodes HTML entities in a string (e.g. &amp; → &, &quot; → ") for safe display as text.
     */
    static decodeHtmlEntities(text: string): string {
        if (!text) return text;
        const doc = new DOMParser().parseFromString(text, 'text/html');
        return doc.documentElement.textContent ?? text;
    }

    /**
     * Ensures a protocol-relative URL (//...) gets https: prefix.
     */
    static fixImageUrl(url: string): string {
        if (url.startsWith('//')) {
            return `https:${url}`;
        }
        return url;
    }

    /**
     * Extracts the first image src and alt from HTML content.
     * Falls back to YouTube thumbnail if no img tag, then placeholder.
     */
    static extractImageFromContent(content: string): { src: string; alt: string } {
        const srcMatch = content.match(/<img[^>]+src=["']([^"']+)["']/i);
        const altMatch = content.match(/<img[^>]+alt=["']([^"']*)["']/i);
        let src: string;
        if (srcMatch?.[1]) {
            src = Utilities.fixImageUrl(srcMatch[1]);
        } else {
            const youtubeMatch = content.match(/youtube[^"']*embed\/([^"']+)/i);
            if (youtubeMatch?.[1]) {
                src = `https://img.youtube.com/vi/${youtubeMatch[1]}/maxresdefault.jpg`;
            } else {
                src = 'assets/img/post-landscape-1.jpg';
            }
        }
        const alt = altMatch?.[1] ?? '';
        return { src, alt };
    }

    /**
     * Strips HTML from content and returns plain text, truncated to 200 characters.
     */
    static extractDescription(content: string): string {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        let text = tempDiv.textContent || tempDiv.innerText || '';
        text = text.replace(/\s+/g, ' ').trim();
        if (text.length > 200) {
            text = text.substring(0, 200) + '...';
        }
        return text || 'No description available';
    }

}
